import React, { useState } from "react";
import styled from "styled-components";
import Chart from "react-apexcharts";

// More subdued color palette
const colors = {
  primary: {
    50: "#F8FAFC",
    100: "#F1F5F9", 
    500: "#64748B",
    600: "#475569",
  },
  accent: {
    500: "#3B82F6",
    600: "#2563EB",
  },
  text: {
    primary: "#1E293B",
    secondary: "#64748B",
    tertiary: "#94A3B8",
  },
  background: {
    main: "#FFFFFF",
    subtle: "#FAFAFA",
  },
  border: {
    light: "#E2E8F0",
    medium: "#CBD5E1",
  },
  status: {
    success: "#059669",
    successBg: "#ECFDF5",
    warning: "#D97706", 
    warningBg: "#FFFBEB",
    error: "#DC2626",
    errorBg: "#FEF2F2",
  }
};

// Mock progress log with member-entered updates
const mockProgressLog = [
  {
    id: 1,
    date: "2024-01-20",
    status: "on-track",
    memberText: "Had a great week implementing the feedback framework. My team really appreciated the structured approach, and I'm seeing more open communication already. The 1:1s are going much better with the new format. Feeling confident about hitting our next milestone.",
    progressPercent: 85
  },
  {
    id: 2,
    date: "2024-01-15",
    status: "on-track", 
    memberText: "Started rolling out the communication charter this week. Got some pushback initially, but after explaining the benefits, the team is on board. Need to work on consistency with the feedback timing, but overall making good progress.",
    progressPercent: 75
  },
  {
    id: 3,
    date: "2024-01-10",
    status: "on-track",
    memberText: "Kicked off the action plan today! Reviewed all the items with my manager and we aligned on priorities. Team seems excited about the changes, especially the mentoring program. Ready to dive in.",
    progressPercent: 60
  },
  {
    id: 4,
    date: "2024-01-05",
    status: "at-risk",
    memberText: "Had some challenges this week with scheduling. The holiday period made it hard to get everyone together for the initial meetings. Planning to catch up next week when everyone is back.",
    progressPercent: 45
  }
];

// Styled Components
const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: "Raleway", sans-serif;
  background: ${colors.background.main};
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: ${colors.background.main};
  border-bottom: 1px solid ${colors.border.light};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: transparent;
  border: none;
  color: ${colors.text.secondary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
  font-family: "Raleway", sans-serif;

  &:hover {
    background: ${colors.primary["50"]};
    color: ${colors.accent["600"]};
  }
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const MemberName = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 4px 0;
`;

const MemberDetails = styled.div`
  font-size: 14px;
  color: ${colors.text.secondary};
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 40px 24px;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

// Dashboard Cards
const DashboardSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 2fr;
  gap: 20px;
  margin-bottom: 16px;
`;

const DashboardCard = styled.div`
  background: ${colors.background.main};
  border: 1px solid ${colors.border.light};
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 80px;
`;

const CardValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.color || colors.text.primary};
  margin-bottom: 6px;
  line-height: 1.1;
`;

const CardLabel = styled.div`
  font-size: 13px;
  color: ${colors.text.secondary};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AnchorCard = styled.div`
  background: ${colors.background.main};
  border: 1px solid ${colors.border.light};
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 80px;
`;

const AnchorTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 4px;
`;

const AnchorSubtitle = styled.div`
  font-size: 14px;
  color: ${colors.text.secondary};
`;

const Section = styled.div`
  background: ${colors.background.main};
  padding: 0;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 32px 0;
  text-align: center;
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 400px;
  
  .apexcharts-canvas {
    margin: 0 auto;
  }
`;

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${colors.background.main};
  border: 1px solid ${colors.border.light};
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  padding: 32px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${colors.border.light};
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: ${colors.text.secondary};
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${colors.text.primary};
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid ${colors.border.light};

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.text.secondary};
`;

const DetailValue = styled.span`
  font-size: 14px;
  color: ${colors.text.primary};
`;

const StatusChip = styled.span`
  padding: 4px 12px;
  border: 1px solid ${colors.border.light};
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    switch(props.status) {
      case 'on-track': return colors.status.successBg;
      case 'at-risk': return colors.status.warningBg;
      case 'off-track': return colors.status.errorBg;
      default: return colors.primary["50"];
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'on-track': return colors.status.success;
      case 'at-risk': return colors.status.warning;
      case 'off-track': return colors.status.error;
      default: return colors.text.secondary;
    }
  }};
`;

const MemberTextDisplay = styled.div`
  font-size: 16px;
  color: ${colors.text.primary};
  line-height: 1.6;
  padding: 20px;
  background: ${colors.background.subtle};
  border-left: 3px solid ${colors.accent["500"]};
  margin: 16px 0 0 0;
  
  &::before {
    content: '"';
    font-size: 20px;
    color: ${colors.text.tertiary};
    margin-right: 4px;
  }
  
  &::after {
    content: '"';
    font-size: 20px;
    color: ${colors.text.tertiary};
    margin-left: 4px;
  }
`;

const PlanDetailView = ({ plan, onBack }) => {
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!plan) return null;

  // Calculate metrics
  const sortedLog = mockProgressLog.sort((a, b) => new Date(a.date) - new Date(b.date));
  const mostRecentEntry = sortedLog[sortedLog.length - 1];
  const averageProgress = Math.round(
    mockProgressLog.reduce((sum, entry) => sum + entry.progressPercent, 0) / mockProgressLog.length
  );

  // Determine overall status color
  const getStatusColor = (progress) => {
    if (progress >= 80) return colors.status.success;
    if (progress >= 60) return colors.accent["500"];
    if (progress >= 40) return colors.status.warning;
    return colors.status.error;
  };

  // Convert progress to status word
  const getStatusText = (progress) => {
    if (progress >= 80) return "On Track";
    if (progress >= 60) return "Good Progress";
    if (progress >= 40) return "At Risk";
    return "Off Track";
  };

  // Prepare chart data
  const chartData = mockProgressLog
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map(entry => ({
      x: new Date(entry.date).getTime(),
      y: entry.progressPercent
    }));

  const handleDataPointClick = (event, chartContext, config) => {
    // Handle different event types from ApexCharts
    let dataPointIndex;
    
    if (config && typeof config.dataPointIndex !== 'undefined') {
      dataPointIndex = config.dataPointIndex;
    } else if (config && typeof config.seriesIndex !== 'undefined' && typeof config.dataPointIndex !== 'undefined') {
      dataPointIndex = config.dataPointIndex;
    } else if (event && event.target) {
      // Try to find the data point from the event
      return; // Skip if we can't determine the data point
    } else {
      return; // Skip if no valid config
    }
    
    if (dataPointIndex >= 0 && dataPointIndex < mockProgressLog.length) {
      const sortedLog = mockProgressLog.sort((a, b) => new Date(a.date) - new Date(b.date));
      const selectedData = sortedLog[dataPointIndex];
      
      setSelectedEntry(selectedData);
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedEntry(null);
  };

  const chartOptions = {
    chart: {
      type: 'line',
      height: 400,
      toolbar: {
        show: false
      },
      zoom: {
        enabled: false
      },
      selection: {
        enabled: false
      },
              events: {
          dataPointSelection: handleDataPointClick,
          markerClick: handleDataPointClick,
          click: handleDataPointClick
        }
    },
    stroke: {
      curve: 'smooth',
      width: 3
    },
    colors: [colors.accent["500"]],
    dataLabels: {
      enabled: true,
      formatter: function(val) {
        return val + '%';
      },
      style: {
        fontSize: '12px',
        fontWeight: '500',
        colors: [colors.text.primary]
      },
      background: {
        enabled: false
      },
      offsetY: -10
    },
    grid: {
      borderColor: colors.border.light,
      strokeDashArray: 3
    },
    xaxis: {
      type: 'datetime',
      labels: {
        style: {
          colors: colors.text.secondary,
          fontSize: '12px'
        },
        format: 'MMM dd'
      },
      axisBorder: {
        color: colors.border.light
      },
      axisTicks: {
        color: colors.border.light
      }
    },
    yaxis: {
      min: 0,
      max: 100,
      labels: {
        style: {
          colors: colors.text.secondary,
          fontSize: '12px'
        },
        formatter: function(val) {
          return val + '%';
        }
      },
      axisBorder: {
        color: colors.border.light
      }
    },
    tooltip: {
      enabled: true,
      theme: 'light',
      style: {
        fontSize: '12px'
      },
      x: {
        format: 'MMM dd, yyyy'
      },
      y: {
        formatter: function(val) {
          return val + '% on track';
        }
      },
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        return `
          <div style="padding: 10px;">
            <div style="font-weight: 500; margin-bottom: 5px;">
              ${new Date(w.globals.seriesX[seriesIndex][dataPointIndex]).toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </div>
            <div style="color: ${colors.accent["500"]}; font-weight: 500;">
              ${series[seriesIndex][dataPointIndex]}% on track
            </div>
            <div style="font-size: 11px; color: ${colors.text.secondary}; margin-top: 5px;">
              Click to view check-in details
            </div>
          </div>
        `;
      }
    },
    markers: {
      size: 8,
      colors: [colors.accent["500"]],
      strokeColors: colors.background.main,
      strokeWidth: 2,
      hover: {
        size: 10
      },
      onClick: handleDataPointClick
    }
  };

  const chartSeries = [{
    name: 'Progress',
    data: chartData
  }];

  return (
    <Container>
      <HeaderSection>
        <BackButton onClick={onBack}>
          ← Back to Group
        </BackButton>
        <HeaderInfo>
          <MemberName>{plan.member_name}</MemberName>
          <MemberDetails>
            {plan.department} • Last updated {new Date(plan.last_update).toLocaleDateString()}
          </MemberDetails>
        </HeaderInfo>
        <div style={{ width: '120px' }}></div>
      </HeaderSection>

      <ContentWrapper>
        <DashboardSection>
          <DashboardCard>
            <CardValue color={getStatusColor(averageProgress)}>
              {getStatusText(averageProgress)}
            </CardValue>
            <CardLabel>Status</CardLabel>
          </DashboardCard>
          
          <DashboardCard>
            <CardValue color={getStatusColor(mostRecentEntry.progressPercent)}>
              {mostRecentEntry.progressPercent}%
            </CardValue>
            <CardLabel>Most Recent</CardLabel>
          </DashboardCard>
          
          <AnchorCard>
            <AnchorTitle>Leadership Development Plan</AnchorTitle>
            <AnchorSubtitle>Focus: Communication & Team Building</AnchorSubtitle>
          </AnchorCard>
        </DashboardSection>

        <Section>
          <SectionTitle>Progress Over Time</SectionTitle>
          <ChartContainer>
            <Chart
              options={chartOptions}
              series={chartSeries}
              type="line"
              height={400}
            />
          </ChartContainer>
        </Section>
      </ContentWrapper>

      {/* Modal */}
      {isModalOpen && selectedEntry && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Check-in Details</ModalTitle>
              <CloseButton onClick={closeModal}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <DetailRow>
                <DetailLabel>Date:</DetailLabel>
                <DetailValue>
                  {new Date(selectedEntry.date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Progress:</DetailLabel>
                <DetailValue>{selectedEntry.progressPercent}% on track</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Status:</DetailLabel>
                <StatusChip status={selectedEntry.status}>
                  {selectedEntry.status.replace('-', ' ')}
                </StatusChip>
              </DetailRow>
              <div>
                <DetailLabel>Member Update:</DetailLabel>
                <MemberTextDisplay>
                  {selectedEntry.memberText}
                </MemberTextDisplay>
              </div>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default PlanDetailView; 